import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", company: "", message: "", website: "" };

export function AskQuestionForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) return toast.error("Please add your name, email and a short question");
    setLoading(true);
    try { await api.post("/contact", form); setDone(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center" data-testid="contact-confirmation">
        <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto" />
        <h3 className="font-display text-xl font-bold mt-3">Question received</h3>
        <p className="text-sm text-zinc-600 mt-2">Thanks, {form.name.split(" ")[0]}. We'll reply to {form.email} within one business day. A copy is on its way to your inbox.</p>
        <Button variant="outline" className="mt-5 h-10 border-zinc-300 font-semibold" onClick={() => { setForm(EMPTY); setDone(false); }} data-testid="contact-another">Ask another question</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 space-y-4" data-testid="contact-form">
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-sm font-semibold">Name *</Label><Input value={form.name} onChange={set("name")} className="h-11 mt-1.5" data-testid="contact-name" /></div>
        <div><Label className="text-sm font-semibold">Email *</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="h-11 mt-1.5" data-testid="contact-email" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-sm font-semibold">Phone <span className="font-normal text-zinc-400">(optional)</span></Label><Input value={form.phone} onChange={set("phone")} className="h-11 mt-1.5" data-testid="contact-phone" /></div>
        <div><Label className="text-sm font-semibold">Company <span className="font-normal text-zinc-400">(optional)</span></Label><Input value={form.company} onChange={set("company")} className="h-11 mt-1.5" data-testid="contact-company" /></div>
      </div>
      <div><Label className="text-sm font-semibold">Your question *</Label><Textarea value={form.message} onChange={set("message")} rows={4} placeholder="Ask about pricing, label stock, imports, your industry — anything." className="mt-1.5" data-testid="contact-message" /></div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Button type="submit" disabled={loading} className="h-11 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="contact-submit">
          {loading ? <Spinner size={18} className="animate-spin" /> : <>Send question <PaperPlaneTilt size={16} weight="bold" /></>}
        </Button>
        <p className="text-xs text-zinc-500">We only use your details to answer you. No newsletters.</p>
      </div>
    </form>
  );
}
